import React, { useState, useRef } from 'react';
import { useApp } from '../../context/AppContext.tsx';
import { MediaShowcaseItem } from '../../types.ts';
import { DEFAULT_MEDIA_SHOWCASE } from '../home/MediaShowcaseSection.tsx';
import { compressImageFile } from '../../utils/mediaOptimizer.ts';
import {
  X,
  Plus,
  Edit2,
  Trash2,
  Upload,
  Image as ImageIcon,
  ArrowUp,
  ArrowDown,
  Check,
  ExternalLink,
  Info,
} from 'lucide-react';

interface MediaShowcaseManagerModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function MediaShowcaseManagerModal({
  isOpen,
  onClose,
}: MediaShowcaseManagerModalProps) {
  const { settings, updateSettingsOnServer, showToast } = useApp();
  const primaryColor = settings?.theme?.primaryColor || '#059669';

  const [mediaList, setMediaList] = useState<MediaShowcaseItem[]>(() => {
    return settings?.mediaShowcase && settings.mediaShowcase.length > 0
      ? settings.mediaShowcase
      : DEFAULT_MEDIA_SHOWCASE;
  });

  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Form State
  const [formData, setFormData] = useState<Omit<MediaShowcaseItem, 'id'>>({
    imageUrl: '',
    title: '',
    caption: '',
    linkUrl: '',
    badge: '',
    order: 1,
  });

  if (!isOpen) return null;

  const handleOpenAddForm = () => {
    setFormData({
      imageUrl: 'https://images.unsplash.com/photo-1528605248644-14dd04022da1?auto=format&fit=crop&w=1200&q=80',
      title: '',
      caption: '',
      linkUrl: '',
      badge: 'SOROTAN',
      order: mediaList.length + 1,
    });
    setEditingId(null);
    setIsEditing(true);
  };

  const handleOpenEditForm = (item: MediaShowcaseItem) => {
    setFormData({
      imageUrl: item.imageUrl,
      title: item.title || '',
      caption: item.caption || '',
      linkUrl: item.linkUrl || '',
      badge: item.badge || '',
      order: item.order || 1,
    });
    setEditingId(item.id);
    setIsEditing(true);
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const compressed = await compressImageFile(file, 1600, 1000, 0.82);
      setFormData((prev) => ({ ...prev, imageUrl: compressed.dataUrl }));
      showToast('Foto berhasil diunggah & dioptimasi!', 'success');
    } catch (err) {
      showToast('Gagal memproses foto', 'error');
    } finally {
      setIsUploading(false);
    }
  };

  const handleSaveMedia = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.imageUrl.trim()) {
      showToast('Foto / media wajib diunggah atau diisi URL!', 'warning');
      return;
    }

    let updated: MediaShowcaseItem[];
    if (editingId) {
      // Edit
      updated = mediaList.map((m) =>
        m.id === editingId ? { ...formData, id: editingId } : m
      );
      showToast('Foto media berhasil diperbarui!', 'success');
    } else {
      // Add
      const newItem: MediaShowcaseItem = {
        ...formData,
        id: `media-${Date.now()}-${Math.random().toString(36).substring(2, 5)}`,
      };
      updated = [...mediaList, newItem];
      showToast('Foto media baru berhasil ditambahkan!', 'success');
    }

    updated.sort((a, b) => (a.order || 0) - (b.order || 0));
    setMediaList(updated);
    await updateSettingsOnServer({ mediaShowcase: updated });
    setIsEditing(false);
    setEditingId(null);
  };

  const handleDeleteMedia = async (id: string, title?: string) => {
    const updated = mediaList.filter((m) => m.id !== id);
    setMediaList(updated);
    await updateSettingsOnServer({ mediaShowcase: updated });
    showToast('Foto berhasil dihapus.', 'info');
  };

  const handleMoveOrder = async (index: number, direction: 'up' | 'down') => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === mediaList.length - 1)
    ) {
      return;
    }
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    const updated = [...mediaList];
    const temp = updated[index];
    updated[index] = updated[targetIndex];
    updated[targetIndex] = temp;

    updated.forEach((m, idx) => {
      m.order = idx + 1;
    });

    setMediaList(updated);
    await updateSettingsOnServer({ mediaShowcase: updated });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white w-full max-w-4xl rounded-3xl shadow-2xl border border-slate-100 overflow-hidden flex flex-col max-h-[92vh]">
        {/* Header */}
        <div className="p-4 sm:p-5 border-b border-slate-100 flex items-center justify-between bg-slate-50">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-2xl flex items-center justify-center text-white shadow-md shrink-0"
              style={{ backgroundColor: primaryColor }}
            >
              <ImageIcon className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-extrabold text-sm sm:text-base text-slate-900 flex items-center gap-2">
                <span>Kelola Grid Media & Foto Beranda</span>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800">
                  {mediaList.length} Foto Aktif
                </span>
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Bisa diisi 1 foto (tampil megah sebagai banner sorotan) atau lebih dari 1 foto (tampil dalam bentuk galeri modern).
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-full hover:bg-slate-200 text-slate-400 hover:text-slate-700 transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-4 sm:p-6 overflow-y-auto space-y-6 flex-1">
          {/* Info Banner about flexible quantity */}
          <div className="p-3.5 bg-emerald-50/80 border border-emerald-200/80 rounded-2xl flex items-start gap-2.5 text-xs text-emerald-950">
            <Info className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
            <div className="leading-relaxed">
              <strong>Fleksibel & Otomatis:</strong> Anda bebas mengisi <strong>1 foto saja</strong> (misal banner promo utama) atau <strong>beberapa foto</strong> (misal dokumentasi momen seru). Sistem akan otomatis menyesuaikan tata letak grid agar selalu tampak proporsional dan estetik di layar HP maupun laptop.
            </div>
          </div>

          {/* Form Mode */}
          {isEditing ? (
            <form onSubmit={handleSaveMedia} className="space-y-5 bg-slate-50 p-5 rounded-2xl border border-slate-200 animate-in zoom-in-98 duration-150">
              <div className="flex items-center justify-between border-b border-slate-200/80 pb-3">
                <h4 className="font-bold text-sm text-slate-900 flex items-center gap-2">
                  <ImageIcon className="w-4 h-4 text-emerald-600" />
                  <span>{editingId ? 'Edit Data Foto / Media' : 'Tambah Foto / Media Baru'}</span>
                </h4>
                <button
                  type="button"
                  onClick={() => setIsEditing(false)}
                  className="text-xs text-slate-500 hover:text-slate-800 font-semibold cursor-pointer"
                >
                  Batal
                </button>
              </div>

              {/* Photo Input & Preview */}
              <div className="space-y-3">
                <label className="block text-xs font-bold text-slate-800">
                  Foto / Media Visual *
                </label>
                <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                  <div className="w-full sm:w-48 aspect-16/10 rounded-2xl overflow-hidden bg-slate-200 border-2 border-slate-300 relative shrink-0 shadow-inner">
                    {formData.imageUrl ? (
                      <img
                        src={formData.imageUrl}
                        alt="Preview"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center text-slate-400">
                        <ImageIcon className="w-6 h-6" />
                        <span className="text-[10px] mt-1">Belum ada foto</span>
                      </div>
                    )}
                    {isUploading && (
                      <div className="absolute inset-0 bg-slate-900/60 flex items-center justify-center text-white text-xs font-bold">
                        Mengompres...
                      </div>
                    )}
                  </div>

                  <div className="flex-1 space-y-2 w-full">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="px-3.5 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs flex items-center gap-1.5 transition-colors cursor-pointer shrink-0 shadow-xs"
                      >
                        <Upload className="w-3.5 h-3.5" />
                        <span>Upload Foto</span>
                      </button>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleFileUpload}
                        className="hidden"
                      />
                      <input
                        type="text"
                        placeholder="Atau tempel URL gambar (https://...)"
                        value={formData.imageUrl}
                        onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
                        className="w-full text-xs bg-white border border-slate-300 px-3 py-2 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-500 font-mono"
                      />
                    </div>
                    <p className="text-[10px] text-slate-400">
                      Format JPG, PNG, atau WebP. Gambar dikompres otomatis agar halaman beranda tetap cepat dan hemat kuota.
                    </p>
                  </div>
                </div>
              </div>

              {/* Title & Badge */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="sm:col-span-2">
                  <label className="block text-xs font-bold text-slate-800 mb-1">
                    Judul / Keterangan Foto
                  </label>
                  <input
                    type="text"
                    placeholder="Contoh: Peluncuran Produk & Dokumentasi Kegiatan 2026"
                    value={formData.title || ''}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full text-xs font-bold text-slate-900 bg-white border border-slate-300 px-3.5 py-2.5 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-800 mb-1">
                    Badge / Kategori Kecil
                  </label>
                  <input
                    type="text"
                    placeholder="Contoh: HIGHLIGHT / PROYEK UTAMA"
                    value={formData.badge || ''}
                    onChange={(e) => setFormData({ ...formData, badge: e.target.value })}
                    className="w-full text-xs font-semibold text-slate-900 bg-white border border-slate-300 px-3.5 py-2.5 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 uppercase"
                  />
                </div>
              </div>

              {/* Caption & Link URL */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-800 mb-1">
                    Deskripsi / Teks Penjelas (Caption)
                  </label>
                  <textarea
                    rows={2}
                    placeholder="Contoh: Dokumentasi kegiatan resmi, presentasi tim, dan agenda strategis..."
                    value={formData.caption || ''}
                    onChange={(e) => setFormData({ ...formData, caption: e.target.value })}
                    className="w-full text-xs text-slate-800 bg-white border border-slate-300 p-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 leading-relaxed"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-800 mb-1">
                    Link URL Klik (Opsional)
                  </label>
                  <input
                    type="text"
                    placeholder="Contoh: https://... atau /produk/layanan-unggulan"
                    value={formData.linkUrl || ''}
                    onChange={(e) => setFormData({ ...formData, linkUrl: e.target.value })}
                    className="w-full text-xs text-slate-800 bg-white border border-slate-300 px-3.5 py-2.5 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 font-mono"
                  />
                  <p className="text-[10px] text-slate-400 mt-1">
                    Jika diisi, pengunjung yang mengklik foto ini akan diarahkan ke link tersebut.
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-end gap-3 pt-3">
                <button
                  type="button"
                  onClick={() => setIsEditing(false)}
                  className="px-4 py-2 rounded-xl text-slate-600 hover:bg-slate-200 text-xs font-bold transition-colors cursor-pointer"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 rounded-xl text-white font-bold text-xs shadow-md flex items-center gap-1.5 transition-all cursor-pointer"
                  style={{ backgroundColor: primaryColor }}
                >
                  <Check className="w-4 h-4" />
                  <span>Simpan Foto Media</span>
                </button>
              </div>
            </form>
          ) : (
            <>
              {/* Action Bar */}
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-slate-700">
                  Daftar Foto / Media Saat Ini ({mediaList.length})
                </span>
                <button
                  type="button"
                  onClick={handleOpenAddForm}
                  className="px-4 py-2 rounded-xl text-white font-bold text-xs shadow-md flex items-center gap-1.5 transition-all cursor-pointer"
                  style={{ backgroundColor: primaryColor }}
                >
                  <Plus className="w-4 h-4" />
                  <span>+ Tambah Foto / Media Baru</span>
                </button>
              </div>

              {/* Media Grid Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {mediaList.map((item, index) => (
                  <div
                    key={item.id}
                    className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-2xs hover:border-emerald-300 transition-all flex flex-col group"
                  >
                    {/* Thumbnail */}
                    <div className="relative aspect-16/10 overflow-hidden bg-slate-100">
                      <img
                        src={item.imageUrl}
                        alt={item.title || 'Foto'}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      {item.badge && (
                        <span
                          className="absolute top-2 left-2 px-2 py-0.5 rounded text-[9px] font-extrabold uppercase tracking-wider text-white shadow-md backdrop-blur-xs"
                          style={{ backgroundColor: primaryColor }}
                        >
                          {item.badge}
                        </span>
                      )}

                      {/* Order Controls */}
                      <div className="absolute top-2 right-2 flex items-center gap-1 bg-slate-900/70 backdrop-blur-xs rounded-lg p-0.5 text-white">
                        <button
                          type="button"
                          disabled={index === 0}
                          onClick={() => handleMoveOrder(index, 'up')}
                          className="p-1 hover:text-emerald-400 disabled:opacity-20 cursor-pointer"
                          title="Pindah ke kiri / awal"
                        >
                          <ArrowUp className="w-3 h-3" />
                        </button>
                        <button
                          type="button"
                          disabled={index === mediaList.length - 1}
                          onClick={() => handleMoveOrder(index, 'down')}
                          className="p-1 hover:text-emerald-400 disabled:opacity-20 cursor-pointer"
                          title="Pindah ke kanan / akhir"
                        >
                          <ArrowDown className="w-3 h-3" />
                        </button>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-3.5 flex-1 flex flex-col justify-between">
                      <div>
                        <h4 className="font-bold text-xs sm:text-sm text-slate-900 truncate">
                          {item.title || '(Tanpa judul)'}
                        </h4>
                        {item.caption && (
                          <p className="text-[11px] text-slate-500 line-clamp-2 mt-1 leading-relaxed">
                            {item.caption}
                          </p>
                        )}
                        {item.linkUrl && (
                          <div className="mt-1.5 flex items-center gap-1 text-[10px] text-emerald-700 font-mono truncate">
                            <ExternalLink className="w-2.5 h-2.5 shrink-0" />
                            <span className="truncate">{item.linkUrl}</span>
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex items-center justify-end gap-2 mt-3 pt-2 border-t border-slate-100">
                        <button
                          type="button"
                          onClick={() => handleOpenEditForm(item)}
                          className="px-2.5 py-1 bg-slate-100 hover:bg-emerald-50 hover:text-emerald-700 text-slate-700 font-bold rounded-lg text-[11px] flex items-center gap-1 transition-colors cursor-pointer"
                        >
                          <Edit2 className="w-3 h-3" />
                          <span>Edit</span>
                        </button>
                        {deleteConfirmId === item.id ? (
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => {
                                handleDeleteMedia(item.id, item.title);
                                setDeleteConfirmId(null);
                              }}
                              className="px-2 py-1 bg-rose-600 hover:bg-rose-700 text-white font-bold rounded-lg text-[10px] cursor-pointer"
                            >
                              Hapus?
                            </button>
                            <button
                              type="button"
                              onClick={() => setDeleteConfirmId(null)}
                              className="px-2 py-1 bg-slate-200 hover:bg-slate-300 text-slate-700 font-bold rounded-lg text-[10px] cursor-pointer"
                            >
                              Batal
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setDeleteConfirmId(item.id)}
                            className="p-1 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer"
                            title="Hapus Foto"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {mediaList.length === 0 && (
                <div className="p-8 text-center text-slate-400 bg-slate-50 rounded-2xl border border-dashed border-slate-300">
                  <ImageIcon className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                  <p className="font-bold text-slate-600 text-sm">Belum ada foto yang ditambahkan</p>
                  <p className="text-xs text-slate-400 mt-1">
                    Klik tombol "+ Tambah Foto / Media Baru" untuk memasukkan foto.
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-100 bg-slate-50 flex items-center justify-between">
          <p className="text-[11px] text-slate-500">
            *Perubahan langsung tampil pada kotak grid beranda website.
          </p>
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-bold transition-colors cursor-pointer"
          >
            Selesai
          </button>
        </div>
      </div>
    </div>
  );
}
